/** A CSV column: a header label and how to extract a plain-text cell from a row. */
export interface CsvColumn<T> {
  label: string;
  value: (row: T) => string;
}

/**
 * First characters Excel / Google Sheets read as the start of a formula when a CSV is opened.
 * `\t` and `\r` are in the set because both are stripped on import, so the character after them
 * becomes the cell's lead — a cell of "\t=cmd|…" evaluates exactly like "=cmd|…".
 */
const FORMULA_LEAD_CHARS = new Set(["=", "+", "-", "@", "\t", "\r"]);

/**
 * A cell that is *only* a number, so a leading `+`/`-` is a sign and not an operator.
 * Allows thousands separators and a fractional part: "-1234.56", "+1,000", "1,234.50".
 *
 * This exemption exists because two shipped columns legitimately lead with a sign:
 * `ClientPurchasesPanel`'s Charged / Stripe fee export the RAW API amount (a refund or a fee
 * reversal is negative) and `JournalEntriesPanel` exports raw `totalDebit`/`totalCredit`.
 * Prefixing those would hand a controller a spreadsheet of text cells that no longer sum.
 * Nothing in this alphabet — digits, commas, one dot, one leading sign — can form a formula:
 * a payload needs an operator, a function name, `!` or `|`, all of which fail the match.
 */
const PLAIN_NUMBER = /^[+-]?\d[\d,]*(?:\.\d+)?$/;

/**
 * Neutralises CSV formula injection (OWASP): prefixes a single quote so the spreadsheet stores
 * the cell as text instead of evaluating it. Applied to every field, because `buildCsv` is shared
 * and at least one caller (thank-you notes) pipes recipient-authored free text through it.
 */
function neutralizeFormula(v: string): string {
  const lead = v[0] ?? "";
  if (!FORMULA_LEAD_CHARS.has(lead) || PLAIN_NUMBER.test(v)) return v;
  return `'${v}`;
}

/** Build a CSV string from column defs + rows, quoting/escaping every field. */
export function buildCsv<T>(columns: CsvColumn<T>[], rows: T[]): string {
  const esc = (v: string) => `"${neutralizeFormula(String(v ?? "")).replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.value(r))).join(","));
  return [header, ...body].join("\n");
}

/** Delay before the object URL is revoked, so the browser can start reading the blob after the click. */
const REVOKE_URL_DELAY_MS = 1000;

/**
 * Trigger a browser download of any `Blob` as `filename`. The anchor's `download` attribute names
 * the file; a response's own `Content-Disposition` plays no part once its bytes are a `Blob`.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_URL_DELAY_MS);
}

/** Trigger a browser download of `csv` as `filename`. */
export function downloadCsv(filename: string, csv: string): void {
  downloadBlob(filename, new Blob([csv], { type: "text/csv;charset=utf-8" }));
}
